// The other end of a share or invite link.
//
// Everything a visitor arriving from someone else's message sees happens here.
// The link's code was captured at startup and parked in storage (see
// utils/shareLinks), so this component only has to ask "is there one waiting?"
// — it never reads the URL, and it works identically whether the person landed
// here signed in, signed out, or came back from a Google round-trip.
//
// The ordering principle: show the workout first, ask for a sign-in second.
// Someone who has never heard of Khronos should be able to see what they were
// sent before deciding whether to create an account for it.

import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import {
  AlertCircle,
  CalendarDays,
  Check,
  Clock,
  Dumbbell,
  Gift,
  Loader2,
  Radio,
  Sparkles,
  UserPlus,
  Users,
} from "lucide-react";
import { WorkoutContext, AppStateContext } from "../../contexts/WorkoutContext";
import { useAuth } from "../../contexts/AuthContext";
import { useSocial } from "../../contexts/SocialContext";
import { useToast } from "../../contexts/ToastContext";
import LoginButton from "../auth/LoginButton";
import SessionPlanDisplay from "../plan/SessionPlanDisplay";
import { getPendingLink, clearPendingLink } from "../../utils/shareLinks";
import {
  getShare,
  claimShare,
  getInvite,
  acceptInvite,
  getWatchSession,
  getLiveSessionInfo,
  getProgrammeInvite,
  joinProgrammeInvite,
} from "../../services/api";
import { calculateTotalDuration, formatDuration } from "../../utils/time";
import { formatAmount } from "../../utils/money";
import { firstName, settingsForShare, shouldUseClaimedShare } from "../../utils/creditShares";

/** "A$2" for a credit invite's amount. */
const creditAmount = (credit) => formatAmount(credit.amount, credit.currency).replace(".00", "");

const KIND_LABELS = {
  session: "Workout",
  plan: "Workout",
  recipe: "Session recipe",
  programme: "Programme",
};

// Why an invite might not be usable, said plainly. Each of these is a real
// state the server distinguishes, and a visitor who followed a link in good
// faith deserves to know which one they hit.
const INVITE_STATE_MESSAGES = {
  revoked: "This invitation has been withdrawn.",
  expired: "This invitation has expired.",
  exhausted: "This invitation has already been used.",
  finished: "That live session has already finished.",
};

const IncomingLinkDialog = () => {
  const [link, setLink] = useState(() => getPendingLink());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const { loadSessionFromData, llmSettings, setLlmSettings } = useContext(WorkoutContext);
  const { setView } = useContext(AppStateContext);
  const { isAuthenticated } = useAuth();
  const { loadFriends, loadGroups, joinLive, watchLive } = useSocial();
  const { showToast } = useToast();

  const close = useCallback(() => {
    // An invite dismissed while signed out stays parked, so signing in later
    // (within the hour the park lasts) brings it back to claim.
    if (isAuthenticated || link?.type !== "invite" || error) clearPendingLink();
    setLink(null);
    setPreview(null);
    setError(null);
    setDone(false);
  }, [isAuthenticated, link, error]);

  // Signing in picks up an invite that was put off with "Not now".
  useEffect(() => {
    if (isAuthenticated) setLink((current) => current || getPendingLink());
  }, [isAuthenticated]);

  // Fetch the preview. Re-runs when auth changes because a 'friends'-only
  // share answers 401 while signed out and resolves once signed in — so the
  // dialog fills itself in the moment sign-in completes, with no second tap.
  useEffect(() => {
    if (!link) return;
    let cancelled = false;

    // A join link is for participants, and participation needs an account.
    // Don't fetch — /api/live/:code answers 401 to a signed-out caller, and
    // "Authentication required" is a worse thing to show someone than the
    // sign-in prompt the footer already offers. The effect re-runs on
    // isAuthenticated, so signing in fills the preview in with no second tap.
    if (link.type === "join" && !isAuthenticated) {
      setLoading(false);
      setPreview(null);
      setError(null);
      return undefined;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchers = {
          invite: getInvite,
          watch: getWatchSession,
          join: getLiveSessionInfo,
          share: getShare,
          together: getProgrammeInvite,
        };
        const data = await (fetchers[link.type] || getShare)(link.code);
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setError(err.message || "This link is not available.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [link, isAuthenticated]);

  const handleUseShare = useCallback(async () => {
    if (!link || !preview) return;
    setBusy(true);
    try {
      const result = await claimShare(link.code);
      const payload = result.payload || {};

      if (result.kind === "recipe") {
        showToast({
          title: "Recipe saved",
          description: `"${payload.name || result.title}" is in Profile → Training.`,
        });
      } else if (result.kind === "programme") {
        showToast({
          title: "Programme added",
          description:
            "It is paused in your library — activate it when you're ready.",
        });
      } else if (payload.plan) {
        // Lands in the Session tab rather than starting immediately: it is
        // someone else's workout, and they may want to adjust it first.
        loadSessionFromData(payload.plan);
        showToast({
          title: "Session loaded",
          description: "Review it in the Session tab, then start when ready.",
        });
      }
      setDone(true);
      close();
    } catch (err) {
      setError(err.message || "Could not load this session.");
    } finally {
      setBusy(false);
    }
  }, [link, preview, loadSessionFromData, showToast, close]);

  // Watching needs no account, so this is the one action in this dialog that
  // never asks for a sign-in.
  const handleWatch = useCallback(async () => {
    if (!link) return;
    setBusy(true);
    try {
      await watchLive(link.code);
      setView("coach");
      setDone(true);
      close();
    } catch (err) {
      setError(err.message || "Could not open this session.");
    } finally {
      setBusy(false);
    }
  }, [link, watchLive, setView, close]);

  const handleJoinLive = useCallback(async () => {
    if (!link) return;
    setBusy(true);
    try {
      await joinLive(link.code);
      setView("coach");
      setDone(true);
      close();
    } catch (err) {
      setError(err.message || "Could not join this session.");
    } finally {
      setBusy(false);
    }
  }, [link, joinLive, setView, close]);

  // Train a programme together (#184): a linked copy lands in their library.
  const handleJoinTogether = useCallback(async () => {
    if (!link) return;
    setBusy(true);
    try {
      const result = await joinProgrammeInvite(link.code);
      if (!result.own) {
        showToast({
          title: result.already ? "You're already in" : "You're in",
          description: result.already
            ? "It's in your programmes."
            : "Your copy is in Programmes. It follows theirs until you change it.",
        });
      }
      setView("programme");
      setDone(true);
      close();
    } catch (err) {
      setError(err.message || "Could not join this programme.");
    } finally {
      setBusy(false);
    }
  }, [link, showToast, setView, close]);

  const handleAcceptInvite = useCallback(async () => {
    if (!link) return;
    setBusy(true);
    try {
      const result = await acceptInvite(link.code);

      if (!result.self) {
        loadFriends?.();
        showToast({
          title: "You're connected",
          description: result.inviter_name
            ? `You and ${result.inviter_name} are now mates.`
            : "Invitation accepted.",
        });
      }

      if (result.kind === "group" && result.group_id) {
        loadGroups?.();
        showToast({
          title: "Group joined",
          description: result.group_name
            ? `You're now in ${result.group_name}.`
            : "You've joined the group.",
        });
      }

      // Credits land as a share, switched on straight away unless they already
      // run on a key of their own, so the first session just works.
      if (result.kind === "credit" && result.credit_share) {
        const share = result.credit_share;
        const switched = shouldUseClaimedShare(llmSettings);
        if (switched) setLlmSettings(settingsForShare(llmSettings, share));
        const amount = formatAmount(share.limitAmount, share.currency).replace(".00", "");
        showToast({
          title: `${amount} of AI credits from ${firstName(share.owner?.name)}`,
          description: switched
            ? "You're all set: write a session and it runs on their credits."
            : "Pick them in Settings › AI engine when you want to use them.",
        });
      }

      if (result.kind === "live" && result.live_join_code) {
        try {
          await joinLive(result.live_join_code);
        } catch (joinErr) {
          // The friendship is already made — a live session that has since
          // filled up or ended must not read as the whole invite failing.
          showToast({
            title: "Could not join the live session",
            description: joinErr.message || "It may have already finished.",
          });
        }
      }

      // A session invite carries its share: hand off to the share flow in the
      // same dialog so accepting and previewing the workout are one journey.
      if (result.kind === "session" && result.share_code) {
        setPreview(null);
        setLink({ type: "share", code: result.share_code });
        setBusy(false);
        return;
      }

      setDone(true);
      close();
    } catch (err) {
      setError(err.message || "Could not accept this invitation.");
    } finally {
      setBusy(false);
    }
  }, [link, loadFriends, loadGroups, joinLive, showToast, close, llmSettings, setLlmSettings]);

  if (!link || done) return null;

  const isInvite = link.type === "invite";
  const isWatch = link.type === "watch";
  const isJoin = link.type === "join";
  const isLive = isWatch || isJoin;
  const isTogether = link.type === "together";
  // A live link's plan sits at the top level of the projection; a share wraps
  // it in a payload.
  const plan = isLive
    ? preview?.plan_data || null
    : preview?.payload?.plan || null;
  const totalSeconds = plan ? calculateTotalDuration(plan) : 0;
  const inviteBlocked =
    isInvite && preview?.state && preview.state !== "open"
      ? INVITE_STATE_MESSAGES[preview.state] ||
        "This invitation is no longer open."
      : null;

  // A live session someone is running right now, or about to. Reuses the
  // plan-and-duration block below via renderSharePreview's tail — what differs
  // is the header, which has to say whether this is a seat or a seat in the
  // audience.
  const renderLivePreview = () => {
    if (!preview) return null;
    const hostName = preview.host_name || preview.host_name_display || null;
    const finished = isWatch && preview.state === "finished";

    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="flex items-center gap-3">
            {preview.host_photo_url ? (
              <img
                src={preview.host_photo_url}
                alt=""
                className="h-10 w-10 rounded-full"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/10">
                <Radio className="h-5 w-5 text-red-500" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium truncate">
                {preview.title || "Live session"}
              </p>
              <p className="text-sm text-muted-foreground">
                {finished
                  ? `${hostName || "They"} have already finished this one.`
                  : isWatch
                    ? `${hostName || "Someone"} is training — follow along live.`
                    : `${hostName || "Someone"} invited you to train with them.`}
              </p>
            </div>
          </div>
          {isWatch && !finished && (
            <p className="text-xs text-muted-foreground">
              You'll see each exercise and timer as they do. Watching only —
              nothing you tap affects their workout, and you don't need an
              account.
            </p>
          )}
        </div>
        {plan && !finished && renderPlanBlock()}
      </div>
    );
  };

  const renderPlanBlock = () => (
    <div className="space-y-3">
      <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {formatDuration(totalSeconds) || "—"}
        </span>
        <span className="inline-flex items-center gap-1">
          <Dumbbell className="h-3.5 w-3.5" />
          {(plan.children || []).length} section
          {(plan.children || []).length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="min-w-0 overflow-x-auto rounded-lg border border-border p-2">
        <SessionPlanDisplay plan={plan} />
      </div>
    </div>
  );

  const renderSharePreview = () => {
    if (!preview) return null;

    if (preview.kind === "recipe") {
      const config = preview.payload?.config || {};
      const pinned = Object.keys(config).filter((k) => k !== "version");
      return (
        <div className="rounded-lg border border-border p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary shrink-0" />
            <span className="font-medium">
              {preview.payload?.name || preview.title}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            A set of generation settings. Your own profile fills in anything it
            doesn't pin, so the session is built for you.
          </p>
          {pinned.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Pins {pinned.length} setting{pinned.length === 1 ? "" : "s"}.
            </p>
          )}
        </div>
      );
    }

    if (preview.kind === "programme") {
      const meta = preview.payload?.programme || {};
      const filled = (preview.payload?.sessions || []).filter(
        (s) => s.plan_data,
      ).length;
      return (
        <div className="rounded-lg border border-border p-4 space-y-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary shrink-0" />
            <span className="font-medium">{meta.title || preview.title}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {meta.duration_weeks && (
              <Badge variant="secondary">{meta.duration_weeks} weeks</Badge>
            )}
            {meta.days_per_week && (
              <Badge variant="secondary">{meta.days_per_week} days/week</Badge>
            )}
            {meta.focus && <Badge variant="secondary">{meta.focus}</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">
            {filled > 0
              ? `${filled} session${filled === 1 ? "" : "s"} already planned.`
              : "The structure is set; sessions are generated as you go."}
          </p>
        </div>
      );
    }

    if (!plan) return null;
    // overflow-x-auto lives inside renderPlanBlock, not on the dialog: a plan
    // too wide for the screen scrolls sideways in its own box rather than
    // stretching the modal. No vertical scroll there — the dialog body already
    // scrolls, and nesting two vertical scrollers is miserable on a phone.
    return renderPlanBlock();
  };

  const renderTogetherPreview = () => {
    if (!preview) return null;
    const mates = (preview.members || []).map((m) => m.name).filter(Boolean);
    return (
      <div className="rounded-lg border border-border p-4 space-y-2">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary shrink-0" />
          <span className="font-medium">{preview.title || "Programme"}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {preview.duration_weeks && <Badge variant="secondary">{preview.duration_weeks} weeks</Badge>}
          {preview.days_per_week && <Badge variant="secondary">{preview.days_per_week} days/week</Badge>}
          {preview.focus && <Badge variant="secondary">{preview.focus}</Badge>}
        </div>
        {mates.length > 0 && (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Users className="h-4 w-4 shrink-0" />
            {`In it: ${mates.join(", ")}`}
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          You get your own copy, on your own days. Sessions anyone builds show up for the rest of you to use or
          adapt.
        </p>
      </div>
    );
  };

  const renderInvitePreview = () => {
    if (!preview) return null;
    const what = {
      app: "to train together on Khronos",
      session: "to try a workout",
      live: "to join a live session",
      group: `to join ${preview.target_title || "a group"}`,
      credit: "to try Khronos",
    }[preview.kind];
    const credit = preview.kind === "credit" ? preview.credit : null;

    return (
      <div className="rounded-lg border border-border p-4 space-y-3">
        <div className="flex items-center gap-3">
          {preview.inviter_photo_url ? (
            <img
              src={preview.inviter_photo_url}
              alt=""
              className="h-10 w-10 rounded-full"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <UserPlus className="h-5 w-5 text-primary" />
            </div>
          )}
          <div className="min-w-0">
            <p className="font-medium truncate">
              {preview.inviter_name || "A Khronos user"}
            </p>
            <p className="text-sm text-muted-foreground">invited you {what}.</p>
          </div>
        </div>
        {credit && (
          <div className="flex items-start gap-3 rounded-lg bg-primary/10 p-3">
            <Gift className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0 space-y-1">
              <p className="font-semibold">
                {`${creditAmount(credit)} of AI credits for ${credit.days} days`}
              </p>
              <p className="text-xs text-muted-foreground">
                Khronos uses AI to write workouts built around you. These
                credits cover that, on {firstName(preview.inviter_name)}&rsquo;s{" "}
                {credit.provider_label} account. Nothing to pay, and no card:
                just sign in with Google to claim them.
              </p>
            </div>
          </div>
        )}
        {preview.note && (
          <p className="text-sm italic text-muted-foreground">
            {`"${preview.note}"`}
          </p>
        )}
        {preview.target_title && preview.kind !== "group" && (
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="truncate">{preview.target_title}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      {/* DialogContent is a CSS grid, whose items default to min-width:auto —
          they refuse to shrink below their content's intrinsic width. A shared
          plan (long labels, deep nesting) is wider than a phone, so without the
          min-w-0 below the grid track stretches past the viewport and clips the
          preview AND the footer buttons off the right edge. The explicit row
          template keeps the header and footer pinned while only the middle
          scrolls. */}
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden p-4 sm:p-6">
        <DialogHeader className="min-w-0 pr-8 text-left">
          <DialogTitle className="min-w-0 break-words pr-2">
            {isInvite
              ? "You've been invited"
              : isTogether
                ? "Train a programme together"
              : isWatch
                ? "Watch this session"
                : isJoin
                  ? "Join this session"
                  : preview?.title || "Shared with you"}
          </DialogTitle>
          <DialogDescription className="min-w-0 break-words">
            {isInvite
              ? preview?.kind === "credit"
                ? isAuthenticated
                  ? "Accept to connect and claim your credits."
                  : "Sign in with Google to claim your credits. It's free."
                : "Accept to connect and pick up whatever they've sent."
              : isTogether
                ? preview?.owner_name
                  ? `${preview.owner_name} invited you to train it together.`
                  : "Someone invited you to train it together."
              : isWatch
                ? "Follow along live. No account needed."
                : isJoin
                  ? "Train alongside them in real time."
                  : preview?.owner_name
                    ? `${KIND_LABELS[preview.kind] || "Workout"} shared by ${preview.owner_name}.`
                    : "Someone shared this with you."}
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 min-h-0 overflow-y-auto space-y-3">
        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {!loading && error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* A join link opened while signed out: nothing was fetched, so say
            what the link is for and let the footer offer the sign-in. */}
        {!loading && !error && isJoin && !isAuthenticated && (
          <div className="rounded-lg border border-border p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-red-500 shrink-0" />
              <span className="font-medium">A live session is running</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Training alongside someone needs an account, so Khronos can keep
              your own history. Sign in to take part.
            </p>
          </div>
        )}

        {!loading && !error && !(isJoin && !isAuthenticated) && (
          <div className="space-y-3">
            {isInvite
              ? renderInvitePreview()
              : isTogether
                ? renderTogetherPreview()
              : isLive
                ? renderLivePreview()
                : renderSharePreview()}

            {inviteBlocked && (
              <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm">
                <AlertCircle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <span>{inviteBlocked}</span>
              </div>
            )}

            {preview?.note && !isInvite && (
              <p className="text-sm italic text-muted-foreground break-words">
                {`"${preview.note}"`}
              </p>
            )}
          </div>
        )}
        </div>

        <DialogFooter className="min-w-0 gap-2 sm:gap-2 [&>*]:w-full sm:[&>*]:w-auto">
          <Button variant="ghost" onClick={close} disabled={busy}>
            Not now
          </Button>

          {/* Watching is the one action here that never asks for an account —
              that is the entire point of a watch link, so it is checked before
              the isAuthenticated branch rather than inside it. */}
          {isWatch ? (
            !error &&
            preview &&
            preview.state !== "finished" && (
              <Button onClick={handleWatch} disabled={busy || loading}>
                {busy ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Radio className="h-4 w-4 mr-2" />
                )}
                Watch live
              </Button>
            )
          ) : !isAuthenticated ? (
            // The preview stays on screen behind this: they can read the whole
            // workout before deciding whether to sign in for it.
            <LoginButton>
              {isInvite
                ? preview?.kind === "credit"
                  ? "Sign in to claim"
                  : "Sign in to accept"
                : isJoin || isTogether
                  ? "Sign in to join"
                  : "Sign in to use this"}
            </LoginButton>
          ) : (
            !error &&
            preview &&
            !inviteBlocked && (
              <Button
                onClick={
                  isInvite
                    ? handleAcceptInvite
                    : isTogether
                      ? handleJoinTogether
                    : isJoin
                      ? handleJoinLive
                      : handleUseShare
                }
                disabled={busy || loading}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {isInvite
                  ? preview?.kind === "credit"
                    ? "Claim credits"
                    : "Accept invitation"
                  : isTogether
                    ? preview?.joined
                      ? "Open it"
                      : "Join"
                  : isJoin
                    ? "Join session"
                    : "Use this"}
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IncomingLinkDialog;
